import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Unlink } from 'lucide-react';

import { Button } from '@/components';
import { cn } from '@/lib/utils';

import { ColorPickerAdvanced } from '../controls/ColorPickerAdvanced';
import { NumberInput } from '../controls/NumberInput';
import { PerSideControl } from '../controls/PerSideControl';
import { StyleToggleButton } from '../controls/StyleToggleButton';
import { BORDER_STYLES } from '../styleConstants';
import type { LayoutElementStyle } from '../styleTypes';

interface BorderSectionProps {
  style: LayoutElementStyle;
  onChange: (updates: Partial<LayoutElementStyle>) => void;
}

function LinkToggle({
  on,
  onToggle,
  onLabel,
  offLabel,
}: {
  on: boolean;
  onToggle: () => void;
  onLabel: string;
  offLabel: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onToggle}
      className={cn(
        'gap-1 rounded px-1.5 py-0.5 text-[10px] font-normal',
        on
          ? 'bg-accent-100 dark:bg-accent-900/30 text-accent-600'
          : 'text-gray-400 hover:text-gray-600',
      )}
    >
      {on ? <Unlink className="w-3 h-3" /> : <Link className="w-3 h-3" />}
      {on ? offLabel : onLabel}
    </Button>
  );
}

export function BorderSection({ style, onChange }: BorderSectionProps) {
  const { t } = useTranslation();
  const p = 'tenant.systemModuleBuilder.style.';
  const [perSideWidth, setPerSideWidth] = useState(
    style.borderTopWidth !== undefined ||
      style.borderRightWidth !== undefined ||
      style.borderBottomWidth !== undefined ||
      style.borderLeftWidth !== undefined,
  );
  const [perCornerRadius, setPerCornerRadius] = useState(
    style.borderTopLeftRadius !== undefined ||
      style.borderTopRightRadius !== undefined ||
      style.borderBottomRightRadius !== undefined ||
      style.borderBottomLeftRadius !== undefined,
  );

  const borderRadius = typeof style.borderRadius === 'number' ? style.borderRadius : undefined;

  return (
    <div className="space-y-3">
      {/* Border Width */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] text-gray-400 uppercase tracking-wider">
            {t(`${p}common.width`)}
          </label>
          <LinkToggle
            on={perSideWidth}
            onToggle={() => setPerSideWidth(!perSideWidth)}
            onLabel={t(`${p}common.uniform`)}
            offLabel={t(`${p}common.separate`)}
          />
        </div>
        {perSideWidth ? (
          <PerSideControl
            top={style.borderTopWidth}
            right={style.borderRightWidth}
            bottom={style.borderBottomWidth}
            left={style.borderLeftWidth}
            onChange={(side, val) => {
              onChange({ [`border${side.charAt(0).toUpperCase() + side.slice(1)}Width`]: val });
            }}
            min={0}
            max={20}
          />
        ) : (
          <NumberInput
            value={style.borderWidth}
            onChange={(v) => onChange({ borderWidth: v })}
            min={0}
            max={20}
            unit="px"
          />
        )}
      </div>

      {/* Border Style */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {t(`${p}border.style`)}
        </label>
        <div className="flex flex-wrap gap-1">
          {BORDER_STYLES.map(({ value, label }) => (
            <StyleToggleButton
              key={value}
              active={(style.borderStyle || 'solid') === value}
              onClick={() => onChange({ borderStyle: value as LayoutElementStyle['borderStyle'] })}
              className="min-w-[3.5rem] text-[10px]"
            >
              {label}
            </StyleToggleButton>
          ))}
        </div>
      </div>

      {/* Border Color */}
      <ColorPickerAdvanced
        color={style.borderColor}
        onChange={(c) => onChange({ borderColor: c })}
        label={t(`${p}common.color`)}
      />

      {/* Border Radius */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] text-gray-400 uppercase tracking-wider">
            {t(`${p}common.borderRadius`)}
          </label>
          <LinkToggle
            on={perCornerRadius}
            onToggle={() => setPerCornerRadius(!perCornerRadius)}
            onLabel={t(`${p}common.uniform`)}
            offLabel={t(`${p}common.separate`)}
          />
        </div>
        {perCornerRadius ? (
          <PerSideControl
            top={style.borderTopLeftRadius}
            right={style.borderTopRightRadius}
            bottom={style.borderBottomRightRadius}
            left={style.borderBottomLeftRadius}
            onChange={(side, val) => {
              const map: Record<string, string> = {
                top: 'borderTopLeftRadius',
                right: 'borderTopRightRadius',
                bottom: 'borderBottomRightRadius',
                left: 'borderBottomLeftRadius',
              };
              const key = map[side];
              if (!key) return;
              onChange({ [key]: val });
            }}
            min={0}
            max={999}
            labels={{ top: 'TL', right: 'TR', bottom: 'BR', left: 'BL' }}
          />
        ) : (
          <NumberInput
            value={borderRadius}
            onChange={(v) => onChange({ borderRadius: v })}
            min={0}
            max={999}
            unit="px"
            showSlider
          />
        )}
      </div>
    </div>
  );
}
